import React, { useId } from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { PHOTOSENSITIVITY_STATUS, useAccountFxPreferences } from '../../../contexts/AccountFxPreferences.jsx';
import { usePrefersReducedMotion } from '../../../hooks/usePrefersReducedMotion.js';
import '../../../styles/PhotosensitivityToggle.css';

const join = (...classes) => classes.filter(Boolean).join(' ');

/**
 * The "Photosensitivity mode" switch (ENG-836; ENG-810's "Reduce effects"
 * switch, renamed). One account-wide setting: it shows and writes the
 * account's `photosensitivityMode` through `useAccountFxPreferences`, which
 * saves it with `PATCH /me/preferences`. Nothing is kept on the device.
 *
 * - `variant="stream"`: in the Shop's FX try-on preview only (no longer on
 *   the stream page, ENG-878). Compact, with a short hint.
 * - `variant="profile"`: in the profile page's Preferences section, with the
 *   longer hint and a "Saved" confirmation.
 *
 * Both say the setting only affects songshake.com effects, never the
 * performer's video (in the hint the switch is described by). The switch only
 * ever shows the account value: when the device asks for reduced motion and
 * the account mode is off it stays off and adds the OS note, since effects are
 * reduced on this device anyway. Disabled while the account is loading or
 * failed to load; a failed save reverts the switch and offers Retry in a
 * polite live region. Signed out there is no account setting, so it renders
 * nothing.
 */
const PhotosensitivityToggle = ({ variant = 'stream', id, switchRef, classNames = {} }) => {
  const { t } = useLocale();
  const {
    status, photosensitivityMode, saveStatus, setPhotosensitivityMode, retrySave,
  } = useAccountFxPreferences();
  const osReducedMotion = usePrefersReducedMotion();
  const fallbackId = useId();
  const baseId = id ?? `photosensitivity-${fallbackId.replace(/:/g, '')}`;

  if (status === PHOTOSENSITIVITY_STATUS.NO_SESSION) return null;

  const on = Boolean(photosensitivityMode);
  const disabled = status !== PHOTOSENSITIVITY_STATUS.READY;
  const showOsNote = osReducedMotion && !on;
  const hintId = `${baseId}-hint`;
  const osNoteId = `${baseId}-os-note`;
  const profile = variant === 'profile';
  const failed = saveStatus === 'error';

  let statusText = null;
  if (failed) statusText = t('photosensitivity.saveError');
  else if (profile && saveStatus === 'saved') statusText = t('photosensitivity.saved');

  return (
    <div
      className={join('photosensitivity-toggle', `photosensitivity-toggle--${variant}`, classNames.root)}
      data-testid="photosensitivity-toggle"
    >
      <button
        type="button"
        role="switch"
        id={`${baseId}-switch`}
        ref={switchRef}
        aria-checked={on}
        aria-describedby={showOsNote ? `${hintId} ${osNoteId}` : hintId}
        disabled={disabled}
        className={join('photosensitivity-switch', on && 'photosensitivity-switch--on')}
        onClick={() => setPhotosensitivityMode(!on)}
        data-testid="photosensitivity-switch"
      >
        <span className="photosensitivity-switch-track" aria-hidden="true">
          <span className="photosensitivity-switch-thumb" />
        </span>
        <span className="photosensitivity-switch-label">{t('photosensitivity.label')}</span>
      </button>
      <p id={hintId} className={join('photosensitivity-hint', classNames.hint)}>
        {t(profile ? 'photosensitivity.profileHint' : 'photosensitivity.streamHint')}
      </p>
      {showOsNote && (
        <p id={osNoteId} className={join('photosensitivity-hint', classNames.hint)} data-testid="photosensitivity-os-note">
          {t('photosensitivity.osNote')}
        </p>
      )}
      <div className="photosensitivity-status-row">
        <p role="status" className={join('photosensitivity-status', classNames.status)} data-testid="photosensitivity-status">
          {statusText}
        </p>
        {failed && (
          <button
            type="button"
            className={join('photosensitivity-retry', classNames.retry)}
            onClick={retrySave}
            data-testid="photosensitivity-retry"
          >
            {t('photosensitivity.retry')}
          </button>
        )}
      </div>
    </div>
  );
};

export default PhotosensitivityToggle;
